import { useEffect } from 'react';
import { useConfig } from '../contexts/ConfigContext';

/**
 * Charge une feuille de style du THÈME ACTIF (`config.activeTheme`) dans le <head>.
 *
 * Le thème actif est piloté côté serveur (POST /api/themes/activate/:id, persisté
 * dans la config) : toutes les pages qui appellent ce hook chargent donc le CSS du
 * même thème, et le commutent automatiquement quand l'admin change de thème.
 * `config.activeTheme = null` → aucune feuille chargée (styles par défaut bundlés).
 *
 * @param {string} cssFile  ex. 'display-screen.css', 'common.css'
 * @author Dr Hamid MADANI <drmdh@msn.com>
 */
/** Thème utilisé tant qu'aucun thème n'a été explicitement configuré (compat
 *  installs existants où `config.activeTheme` est absent). `null` explicite =
 *  thème désactivé → aucune feuille chargée. */
const DEFAULT_THEME = 'algerie-telecom';

export const useThemeCSS = (cssFile) => {
  const { config } = useConfig();
  // undefined (jamais configuré) → thème par défaut ; null (désactivé) → aucun.
  const activeTheme = config?.activeTheme === undefined ? DEFAULT_THEME : config.activeTheme;

  useEffect(() => {
    // Pas de thème actif : on ne charge rien.
    if (!activeTheme) return undefined;

    const linkId = `theme-css-${cssFile.replace(/[^a-zA-Z0-9]/g, '-')}`;
    const href = `/themes/${activeTheme}/css/${cssFile}`;

    // Réutilise la balise existante si déjà présente (un même CSS peut être
    // demandé par plusieurs composants) ; sinon on la crée.
    let link = document.getElementById(linkId);
    let createdHere = false;
    if (!link) {
      link = document.createElement('link');
      link.id = linkId;
      link.rel = 'stylesheet';
      link.type = 'text/css';
      document.head.appendChild(link);
      createdHere = true;
    }
    // (Re)cible vers le thème actif — gère le changement de thème à chaud.
    if (link.getAttribute('href') !== href) link.setAttribute('href', href);

    // On ne retire la feuille que si CET appel l'a créée (évite de l'arracher
    // sous les pieds d'un autre composant qui l'utilise encore).
    return () => {
      if (createdHere) {
        const el = document.getElementById(linkId);
        if (el) el.remove();
      }
    };
  }, [cssFile, activeTheme]);
};

export default useThemeCSS;
